import { Select, Table, Tag } from "antd";
import { useTranslation } from "react-i18next";

import type { ColumnsType } from "antd/es/table";
import { IRedeemCoupon } from "@interfaces";
import { theme, useNotify } from "@utils";

import { memo } from "react";
import { enumStatus, formatDateTime, serviceCategory } from "@configs";
import { useDispatch } from "react-redux";
import { setLoading } from "@redux";
import dayjs from "dayjs";
import { clinicApi } from "@api";

interface IProps {
  data: IRedeemCoupon[];
  callback: () => void;
}
export const PatientTable = memo(({ data, callback }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { success, error } = useNotify();

  const dataSource: IRedeemCoupon[] = data;
  const color = (status: enumStatus) => {
    switch (status) {
      case enumStatus.REDEEMED:
        return theme.colors.main;
        break;
      case enumStatus.REJECT:
        return theme.colors.expired;
      default:
        return theme.colors.yellow;
        break;
    }
  };
  const columns: ColumnsType<IRedeemCoupon> = [
    {
      title: t("page.date"),
      dataIndex: "date",
      key: "1",
      render: (date) => <span>{dayjs(date).format(formatDateTime) || t("page.empty")}</span>,
    },
    {
      title: t("page.coupon_code"),
      dataIndex: "couponCode",
      key: "2",
    },
    {
      title: t("page.patient_name"),
      dataIndex: "name",
      key: "3",
    },
    {
      title: t("page.patient_date_of_birth"),
      dataIndex: "dateOfBirth",
      key: "4",
      render: (dateOfBirth) =>
        dateOfBirth ? dayjs(dateOfBirth).format(formatDateTime) : t("page.empty"),
    },
    {
      title: t("page.status"),
      dataIndex: "status",
      key: "5",
      render: (status) => <Tag color={color(status)}>{status}</Tag>,
    },
    {
      title: t("page.doctor_comment"),
      dataIndex: "doctorComment",
      key: "6",
      render: (doctorComment) => <span>{doctorComment || t("page.empty")}</span>,
    },
    {
      title: t("page.service_category"),
      dataIndex: "category",
      key: "7",
      render: (_, record) => (
        <>
          {
            record?.category ?
            <span>{record?.category}</span>
              :
            <Select
            onChange={(value: string) => onChange(value, record)}
            value={record?.category}
            disabled={!!record?.category}
            style={{ minWidth: "111px" }}
            >
              {serviceCategory.map((item) => (
                <Select.Option value={item} key={item}>
                  {item}
                </Select.Option>
              ))}
            </Select>
          }
        </>
      ),
    },
  ];

  const onChange = async (value: string, record: IRedeemCoupon) => {
    try {
      dispatch(setLoading(true));
      await clinicApi.category({
        category: value,
        couponCode: record?.couponCode,
      });
      callback();
      success(t("message.update.success"));
    } catch (err: any) {
      error(t("message.update.fail"));
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Table dataSource={dataSource} rowKey={"couponFamilyId"} columns={columns} pagination={false} />
  );
});
